import { useRef, useState } from "react";
import { deleteFile, uploadFile } from "@/api/files";
import type { Attachment } from "@/api/content";
import { useLang } from "@/i18n/LanguageContext";

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function AttachmentEditor({
  moduleId,
  attachments,
  onChange,
}: {
  moduleId: string;
  attachments: Attachment[];
  onChange: (next: Attachment[]) => void;
}) {
  const { t } = useLang();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const att = await uploadFile(moduleId, file);
      onChange([...attachments, att]);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Upload mislukt", "Upload failed"));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function remove(i: number) {
    const att = attachments[i];
    onChange(attachments.filter((_, j) => j !== i));
    try {
      await deleteFile(att.file_id);
    } catch {
      /* metadata removal from content is what matters; orphaned bytes are harmless */
    }
  }

  const setCaption = (i: number, caption: string) =>
    onChange(attachments.map((a, j) => (j === i ? { ...a, caption } : a)));

  return (
    <div style={{ marginTop: 18 }}>
      <span style={{ fontWeight: 600, fontSize: 14.5, color: "var(--ink)" }}>{t("Bijlagen (PDF / afbeelding)", "Attachments (PDF / image)")}</span>
      {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}

      {attachments.map((a, i) => (
        <div key={a.file_id} className="attach-row">
          <span className="attach-name" title={a.filename}>📎 {a.filename} <span className="attach-meta">{humanSize(a.size)}</span></span>
          <input
            className="attach-caption"
            value={a.caption ?? ""}
            onChange={(e) => setCaption(i, e.target.value)}
            placeholder={t("Bijschrift (optioneel)", "Caption (optional)")}
          />
          <button className="icon-btn" title={t("Verwijderen", "Remove")} onClick={() => remove(i)}>×</button>
        </div>
      ))}

      <div style={{ marginTop: 10 }}>
        <input ref={inputRef} type="file" accept=".pdf,image/png,image/jpeg,image/gif,image/webp,application/pdf" onChange={onPick} disabled={busy} hidden />
        <button className="btn btn-outline" type="button" disabled={busy} onClick={() => inputRef.current?.click()}>
          {busy ? t("Uploaden…", "Uploading…") : t("+ Bestand uploaden", "+ Upload file")}
        </button>
      </div>
    </div>
  );
}
